// views/LeadsByDayReport.js
import React, { useState, useEffect } from "react";
import {
    Card,
    CardHeader,
    CardBody,
    Container,
    Row,
    Col,
    Alert,
    Table
} from "reactstrap";
import { Line, Bar } from "react-chartjs-2";
import {
    chartOptions,
    parseOptions,
} from "variables/charts.js";
import Header from "components/Headers/Header.js";

const LeadsByDayReport = () => {
    const [selectedDate, setSelectedDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 1);
        return d.toISOString().split('T')[0];
    });

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (window.Chart) {
            parseOptions(window.Chart, chartOptions());
        }

        const fetchLeads = async () => {
            setLoading(true);
            try {
                const token = localStorage.getItem("token");
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/active-campaign/leads-by-day?date=${selectedDate}`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );

                if (!response.ok) {
                    throw new Error("Falha ao buscar dados de leads");
                }

                const result = await response.json();
                setData(result);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchLeads();
    }, [selectedDate]);

    // Configuração do Gráfico (Barra por Tag)
    const chartData = React.useMemo(() => {
        return {
            labels: data.map(d => d.tag),
            datasets: [
                {
                    label: "Leads",
                    data: data.map(d => d.count),
                    backgroundColor: "#11cdef",
                    borderColor: "#11cdef",
                    borderWidth: 1,
                },
            ],
        };
    }, [data]);

    // Ordenação
    const [sortConfig, setSortConfig] = useState({ key: 'count', direction: 'desc' });

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const sortedData = React.useMemo(() => {
        let sortableItems = [...data];
        if (sortConfig.key !== null) {
            sortableItems.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];

                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableItems;
    }, [data, sortConfig]);

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return <i className="fas fa-sort text-muted ml-1"></i>;
        return sortConfig.direction === 'asc'
            ? <i className="fas fa-sort-up ml-1"></i>
            : <i className="fas fa-sort-down ml-1"></i>;
    };

    return (
        <>
            <Header />
            <Container className="mt--7" fluid>
                <Row>
                    <div className="col">
                        <Card className="shadow">
                            <CardHeader className="bg-transparent">
                                <Row className="align-items-center">
                                    <Col>
                                        <h6 className="text-uppercase text-muted ls-1 mb-1">
                                            Visão Geral
                                        </h6>
                                        <h2 className="mb-0">Leads por Tag ({new Date(selectedDate).toLocaleDateString()})</h2>
                                    </Col>
                                    <Col className="text-right">
                                        <div className="d-flex justify-content-end align-items-center">
                                            <label className="mr-2 mb-0 text-sm">Data:</label>
                                            <input
                                                type="date"
                                                className="form-control form-control-sm"
                                                style={{ width: '150px' }}
                                                value={selectedDate}
                                                onChange={(e) => setSelectedDate(e.target.value)}
                                            />
                                        </div>
                                    </Col>
                                </Row>
                            </CardHeader>
                            <CardBody>
                                {loading ? (
                                    <div className="text-center p-5">
                                        <i className="fas fa-spinner fa-spin fa-2x"></i>
                                    </div>
                                ) : error ? (
                                    <Alert color="danger">{error}</Alert>
                                ) : (
                                    <div className="chart">
                                        <div style={{ height: '350px' }}>
                                            <Bar
                                                data={chartData}
                                                options={{
                                                    maintainAspectRatio: false,
                                                    scales: {
                                                        yAxes: [{ ticks: { beginAtZero: true } }]
                                                    }
                                                }}
                                            />
                                        </div>
                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    </div>
                </Row>

                <Row className="mt-5">
                    <Col className="mb-5 mb-xl-0" xl="12">
                        <Card className="shadow">
                            <CardHeader className="border-0">
                                <Row className="align-items-center">
                                    <div className="col">
                                        <h3 className="mb-0">Detalhamento Diário por Tag</h3>
                                    </div>
                                </Row>
                            </CardHeader>
                            <Table className="align-items-center table-flush" responsive>
                                <thead className="thead-light">
                                    <tr>
                                        <th scope="col" style={{ cursor: 'pointer' }} onClick={() => handleSort('date')}>
                                            Data {getSortIcon('date')}
                                        </th>
                                        <th scope="col" style={{ cursor: 'pointer' }} onClick={() => handleSort('tag')}>
                                            Tag {getSortIcon('tag')}
                                        </th>
                                        <th scope="col" style={{ cursor: 'pointer' }} onClick={() => handleSort('count')}>
                                            Leads {getSortIcon('count')}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedData.map((item, index) => (
                                        <tr key={index}>
                                            <th scope="row">
                                                {new Date(item.date).toLocaleDateString()}
                                            </th>
                                            <td>
                                                <span className="badge badge-pill badge-primary" style={{ fontSize: '12px' }}>
                                                    {item.tag}
                                                </span>
                                            </td>
                                            <td>
                                                {item.count}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </>
    );
};

export default LeadsByDayReport;
